import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updatePassword 
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Login do usuário
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Buscar dados do usuário no Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (userDoc.exists()) {
      const userData = {
        uid: user.uid,
        email: user.email,
        ...userDoc.data()
      };
      
      // Se o usuário é de uma empresa, verificar se a empresa está ativa
      if (userData.role === 'company' && userData.companyId) {
        const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
        
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          
          if (!companyData.active) {
            // Fazer logout se a empresa estiver desabilitada
            await signOut(auth);
            throw new Error('Sua empresa está desabilitada. Entre em contato com o administrador para tratar de problemas sobre o acesso.');
          }
          
          // Registrar último acesso da empresa
          await updateDoc(doc(db, 'companies', userData.companyId), {
            lastAccess: new Date().toISOString(),
            lastAccessBy: user.email
          });
        } else {
          // Fazer logout se a empresa não existir
          await signOut(auth);
          throw new Error('Empresa não encontrada. Entre em contato com o administrador.');
        }
      }
      
      // Verificar se é primeiro login (senha provisória)
      if (userData.needsPasswordChange && userData.isFirstLogin) {
        userData.isFirstLogin = true;
      } else {
        // Se não é mais primeiro login, remover a flag
        userData.needsPasswordChange = false;
      }
      
      return userData;
    } else {
      throw new Error('Dados do usuário não encontrados');
    }
  } catch (error) {
    throw new Error(error.message);
  }
};

// Logout do usuário
export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw new Error(error.message);
  }
};

// Criar conta de empresa (versão para admin)
export const createCompanyAccountForAdmin = async (email, password, companyData) => {
  try {
    // Criar nova conta temporariamente
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;
    
    // Criar documento do usuário
    await setDoc(doc(db, 'users', newUser.uid), {
      email: newUser.email,
      role: 'company',
      companyId: newUser.uid,
      createdAt: new Date().toISOString(),
      needsPasswordChange: true, // Marcar que precisa trocar senha
      isFirstLogin: true, // Marcar como primeiro login
      ...companyData
    });
    
    // Criar documento da empresa
    await setDoc(doc(db, 'companies', newUser.uid), {
      id: newUser.uid,
      email: newUser.email,
      active: companyData.active !== undefined ? companyData.active : true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...companyData
    });
    
    // Fazer logout da conta da empresa criada para que o admin possa continuar
    await signOut(auth);
    
    return {
      uid: newUser.uid,
      email: newUser.email,
      role: 'company',
      companyId: newUser.uid,
      needsPasswordChange: true,
      isFirstLogin: true,
      ...companyData
    };
  } catch (error) {
    throw error;
  }
};

// Criar conta de empresa
export const createCompanyAccount = async (email, password, companyData) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Criar documento do usuário
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      role: 'company',
      companyId: user.uid,
      createdAt: new Date().toISOString(),
      needsPasswordChange: true, // Marcar que precisa trocar senha
      isFirstLogin: true, // Marcar como primeiro login
      ...companyData
    });
    
    // Criar documento da empresa
    await setDoc(doc(db, 'companies', user.uid), {
      id: user.uid,
      email: user.email,
      active: companyData.active !== undefined ? companyData.active : true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...companyData
    });
    
    // Fazer logout da conta criada para que o admin continue logado
    await signOut(auth);
    
    return {
      uid: user.uid,
      email: user.email,
      role: 'company',
      companyId: user.uid,
      needsPasswordChange: true,
      isFirstLogin: true,
      ...companyData
    };
  } catch (error) {
    throw new Error(error.message);
  }
};

// Verificar se usuário existe
export const checkUserExists = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar usuário:', error);
    return false;
  }
};

// Trocar senha (primeiro login)
export const changePassword = async (newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    // Atualizar senha no Firebase Auth
    await updatePassword(user, newPassword);

    // Marcar que a senha foi alterada no Firestore
    await updateDoc(doc(db, 'users', user.uid), {
      needsPasswordChange: false,
      isFirstLogin: false,
      passwordChangedAt: new Date().toISOString()
    });

    return true;
  } catch (error) {
    throw new Error(error.message);
  }
};

// Observer de mudanças de autenticação
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = {
            uid: user.uid,
            email: user.email,
            ...userDoc.data()
          };
          
          // Se o usuário é de uma empresa, verificar se a empresa está ativa
          if (userData.role === 'company' && userData.companyId) {
            const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
            
            if (companyDoc.exists()) {
              const companyData = companyDoc.data();
              
              if (!companyData.active) {
                // Fazer logout se a empresa estiver desabilitada
                await signOut(auth);
                callback(null);
                return;
              }
            } else {
              // Fazer logout se a empresa não existir
              await signOut(auth);
              callback(null);
              return;
            }
          }
          
          callback(userData);
        } else {
          callback(null);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        callback(null);
      }
    } else {
      callback(null);
    }
  });
};

// Obter usuário atual
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = {
        uid: user.uid,
        email: user.email,
        ...userDoc.data()
      };
      
      // Se o usuário é de uma empresa, verificar se a empresa está ativa
      if (userData.role === 'company' && userData.companyId) {
        const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
        
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          
          if (!companyData.active) {
            // Fazer logout se a empresa estiver desabilitada
            await signOut(auth);
            return null;
          }
        } else {
          // Fazer logout se a empresa não existir
          await signOut(auth);
          return null;
        }
      }
      
      return userData;
    }
    return null;
  } catch (error) {
    console.error('Erro ao obter usuário atual:', error);
    return null;
  }
};

// Criar conta de teste (trial)
export const createTrialAccount = async (email, password, companyData) => {
  let userCreated = null;
  
  try {
    // Primeiro, verificar se já existe um usuário com este email no Authentication
    // mas sem dados no Firestore (usuário órfão)
    try {
      const existingUserQuery = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const existingUsers = await getDocs(existingUserQuery);
      
      if (!existingUsers.empty) {
        throw new Error('Este email já está cadastrado no sistema');
      }
    } catch (queryError) {
      // Se a query falhar, continuamos com o processo normal
      console.warn('Aviso: Não foi possível verificar usuários existentes:', queryError);
    }
    
    // Criar nova conta no Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;
    userCreated = newUser; // Guardar referência para possível rollback
    
    // Calcular data de expiração do trial (5 dias a partir de hoje)
    const trialStartDate = new Date();
    const trialEndDate = new Date();
    trialEndDate.setDate(trialStartDate.getDate() + 5);
    
    // Dados do usuário para criar
    const userData = {
      email: newUser.email,
      role: 'company',
      companyId: newUser.uid,
      createdAt: new Date().toISOString(),
      isTrial: true,
      trialStartDate: trialStartDate.toISOString(),
      trialEndDate: trialEndDate.toISOString(),
      trialDaysRemaining: 5,
      active: true,
      ...companyData
    };
    
    // Dados da empresa para criar
    const companyDataToSave = {
      id: newUser.uid,
      email: newUser.email,
      active: true,
      isTrial: true,
      trialStartDate: trialStartDate.toISOString(),
      trialEndDate: trialEndDate.toISOString(),
      trialDaysRemaining: 5,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...companyData
    };
    
    try {
      // Criar documento do usuário com timeout
      const userPromise = setDoc(doc(db, 'users', newUser.uid), userData);
      const userTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout ao criar usuário')), 10000)
      );
      await Promise.race([userPromise, userTimeout]);
      
      // Criar documento da empresa com timeout  
      const companyPromise = setDoc(doc(db, 'companies', newUser.uid), companyDataToSave);
      const companyTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout ao criar empresa')), 10000)
      );
      await Promise.race([companyPromise, companyTimeout]);
      
    } catch (firestoreError) {
      console.error('Erro ao criar documentos no Firestore:', firestoreError);
      
      // Tentar deletar o usuário do Authentication em caso de falha no Firestore
      try {
        await newUser.delete();
        console.log('Usuário removido do Authentication após falha no Firestore');
      } catch (deleteError) {
        console.error('Não foi possível remover usuário do Authentication:', deleteError);
        // Neste caso, temos um usuário órfão que precisará ser limpo manualmente
      }
      
      throw new Error('Erro ao salvar dados da empresa. Tente novamente.');
    }
    
    // Fazer logout da conta criada (o usuário precisará fazer login)
    await signOut(auth);
    
    return {
      id: newUser.uid,
      email: newUser.email,
      isTrial: true,
      trialEndDate: trialEndDate.toISOString()
    };
    
  } catch (error) {
    console.error('Error creating trial account:', error);
    
    // Tratamento específico para diferentes tipos de erro
    if (error.code === 'auth/email-already-in-use') {
      // Verificar se é um usuário órfão e tentar recuperar
      try {
        await handleOrphanedUser(email, password, companyData);
        return await createTrialAccount(email, password, companyData);
      } catch (orphanError) {
        throw new Error('Este email já está em uso. Se você teve problemas no cadastro anterior, aguarde alguns minutos e tente novamente.');
      }
    } else if (error.code === 'auth/weak-password') {
      throw new Error('A senha deve ter pelo menos 6 caracteres');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Email inválido');
    } else if (error.code === 'auth/network-request-failed') {
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    }
    
    throw new Error(error.message || 'Erro ao criar conta de teste');
  }
};

// Função helper para lidar com usuários órfãos (existem no Authentication mas não no Firestore)
const handleOrphanedUser = async (email, password, companyData) => {
  try {
    // Tentar fazer login com o usuário órfão
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Verificar se realmente não existem dados no Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const companyDoc = await getDoc(doc(db, 'companies', user.uid));
    
    if (!userDoc.exists() && !companyDoc.exists()) {
      // É realmente um usuário órfão, vamos completar o cadastro
      console.log('Usuário órfão detectado, completando cadastro...');
      
      // Calcular data de expiração do trial (5 dias a partir de hoje)
      const trialStartDate = new Date();
      const trialEndDate = new Date();
      trialEndDate.setDate(trialStartDate.getDate() + 5);
      
      // Criar documento do usuário
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        role: 'company',
        companyId: user.uid,
        createdAt: new Date().toISOString(),
        isTrial: true,
        trialStartDate: trialStartDate.toISOString(),
        trialEndDate: trialEndDate.toISOString(),
        trialDaysRemaining: 5,
        active: true,
        ...companyData
      });
      
      // Criar documento da empresa
      await setDoc(doc(db, 'companies', user.uid), {
        id: user.uid,
        email: user.email,
        active: true,
        isTrial: true,
        trialStartDate: trialStartDate.toISOString(),
        trialEndDate: trialEndDate.toISOString(),
        trialDaysRemaining: 5,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...companyData
      });
      
      // Fazer logout
      await signOut(auth);
      
      return {
        id: user.uid,
        email: user.email,
        isTrial: true,
        trialEndDate: trialEndDate.toISOString(),
        recovered: true
      };
    } else {
      // Usuário já tem dados, não é órfão
      await signOut(auth);
      throw new Error('Usuário já cadastrado completamente');
    }
  } catch (error) {
    console.error('Erro ao tratar usuário órfão:', error);
    throw error;
  }
};

// Atualizar status do trial
export const updateTrialStatus = async (userId, isActive) => {
  try {
    // Atualizar usuário
    await updateDoc(doc(db, 'users', userId), {
      active: isActive,
      updatedAt: new Date().toISOString()
    });
    
    // Atualizar empresa
    await updateDoc(doc(db, 'companies', userId), {
      active: isActive,
      updatedAt: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating trial status:', error);
    return false;
  }
};

// Ativar período de trial para empresa existente (função para admin)
export const activateTrialForCompany = async (companyId, trialDays = 5) => {
  try {
    const trialStartDate = new Date();
    const trialEndDate = new Date();
    trialEndDate.setDate(trialStartDate.getDate() + trialDays);
    
    // Verificar se os documentos existem antes de atualizar
    const userRef = doc(db, 'users', companyId);
    const companyRef = doc(db, 'companies', companyId);
    
    const userDoc = await getDoc(userRef);
    const companyDoc = await getDoc(companyRef);
    
    if (!userDoc.exists()) {
      throw new Error('Usuário não encontrado');
    }
    
    if (!companyDoc.exists()) {
      throw new Error('Empresa não encontrada');
    }
    
    // Atualizar usuário
    await updateDoc(userRef, {
      isTrial: true,
      trialStartDate: trialStartDate.toISOString(),
      trialEndDate: trialEndDate.toISOString(),
      trialDaysRemaining: trialDays,
      active: true,
      updatedAt: new Date().toISOString()
    });
    
    // Atualizar empresa
    await updateDoc(companyRef, {
      isTrial: true,
      trialStartDate: trialStartDate.toISOString(),
      trialEndDate: trialEndDate.toISOString(),
      trialDaysRemaining: trialDays,
      active: true,
      updatedAt: new Date().toISOString()
    });
    
    return {
      success: true,
      trialEndDate: trialEndDate.toISOString(),
      daysRemaining: trialDays
    };
  } catch (error) {
    console.error('Error activating trial for company:', error);
    throw new Error(`Erro ao ativar período de teste: ${error.message}`);
  }
};

// Função administrativa para limpar usuários órfãos
export const cleanOrphanedUsers = async () => {
  try {
    // Esta função deve ser executada apenas por administradores
    // Buscar todos os usuários do Authentication e verificar se têm dados no Firestore
    
    console.log('Iniciando limpeza de usuários órfãos...');
    
    // Esta é uma função administrativa que deve ser executada manualmente quando necessário
    // Não implementaremos a lógica automática por questões de segurança
    
    return {
      success: true,
      message: 'Função de limpeza deve ser executada manualmente via Firebase Console'
    };
  } catch (error) {
    console.error('Erro na limpeza de usuários órfãos:', error);
    throw new Error(`Erro na limpeza: ${error.message}`);
  }
};

// Verificar se um usuário existe no Authentication mas não no Firestore
export const checkUserIntegrity = async (email) => {
  try {
    // Buscar no Firestore
    const userQuery = query(
      collection(db, 'users'),
      where('email', '==', email)
    );
    const userDocs = await getDocs(userQuery);
    
    return {
      email,
      existsInFirestore: !userDocs.empty,
      firestoreData: userDocs.empty ? null : userDocs.docs[0].data()
    };
  } catch (error) {
    console.error('Erro ao verificar integridade do usuário:', error);
    return {
      email,
      error: error.message
    };
  }
};
